<template>

    <button
        class="btn"
        :class="classes"
        :style="styles"
        :disabled="loading || disabled"
        :aria-expanded="ariaExpanded"
        :aria-haspopup="ariaHaspopup"
        :aria-label="ariaLabel"
    >
        <div>
            <span>
                <i v-if="icon" :class="`${icon} mr-${iconMargin}`" :style="{ color: iconColor }" aria-hidden="true" />
                <slot></slot>
                <i v-if="arrow" :class="`fas fa-arrow-right ml-${iconMargin}`" aria-hidden="true" />
            </span>
        </div>
        <div v-if="loading" class="btn-loader">
            <v-loader v-if="['primary', 'danger', 'success', 'custom'].includes(theme)" size="sm" theme="light" />
            <v-loader v-else :size="size" />
        </div>
    </button>

</template>

<style scoped lang="scss">

    .btn {
        @include font-semibold(14px);
        align-items: center;
        appearance: none;
        border: none;
        box-shadow: none;
        cursor: pointer;
        display: flex;
        flex-shrink: 0;
        justify-content: center;
        margin: 0;
        outline: none;
        overflow: hidden;
        position: relative;
        text-decoration: none;
        transition: background .3s, border .3s, color .3s, opacity .3s;
        text-align: center;
        text-decoration: none;
        user-select: none;
        &:disabled {
            opacity: 0.7;
        }
        &:hover, &:active, &.active {
            opacity: 0.9;
        }
        .btn-loader {
            margin-left: 10px;
        }
    }

    .btn {
        &.btn-xs {
            border-radius: 5px;
            font-size: 10px;
            height: 30px;
            padding: 0 10px;
            &.btn-rounded {
                border-radius: 15px;
            }
        }
        &.btn-sm {
            border-radius: 5px;
            font-size: 11px;
            height: 40px;
            padding: 0 15px;
            &.btn-rounded {
                border-radius: 20px;
            }
        }
        &.btn-md {
            border-radius: 5px;
            font-size: 14px;
            height: 50px;
            padding: 0 30px;
            &.btn-rounded {
                border-radius: 25px;
            }
        }
        &.btn-lg {
            border-radius: 5px;
            font-size: 16px;
            height: 60px;
            padding: 0 30px;
            &.btn-rounded {
                border-radius: 30px;
            }
        }
        &.btn-xl {
            border-radius: 5px;
            font-size: 16px;
            height: 70px;
            padding: 0 30px;
            &.btn-rounded {
                border-radius: 35px;
            }
        }
    }

    .btn {
        &.btn-primary {
            background-color: $color-primary;
            border: 1px solid $color-primary;
            color: $color-white;
            &:focus {
                background-color: $color-primary;
                color: $color-white;
            }
            &:hover, &:active, &.active {
                background-color: $color-primary;
                color: $color-white;
            }
        }
        &.btn-trans {
            background-color: $color-white;
            color: $color-text;
            &:focus {
                background-color: $color-white;
                color: $color-primary;
            }
            &:hover, &:active, &.active {
                background-color: $color-white;
                color: $color-primary;
            }
        }
        &.btn-white {
            background-color: $color-white;
            border: 1px solid $color-rule;
            color: $color-text;
            &:focus {
                background-color: $color-white;
                border: 1px solid $color-rule;
                color: $color-text;
            }
            &:hover, &:active {
                background-color: $color-white;
                border: 1px solid $color-text;
                color: $color-text;
            }
            &.active {
                border: 1px solid $color-primary;
                color: $color-primary;
            }
        }
        &.btn-white-primary {
            background-color: $color-white;
            border: 1px solid $color-rule;
            color: $color-primary;
            &:focus {
                background-color: $color-white;
                border: 1px solid $color-rule;
                color: $color-primary;
            }
            &:hover, &:active, &.active {
                background-color: $color-white;
                border: 1px solid $color-primary;
                color: $color-primary;
            }
        }
        &.btn-white-danger {
            background-color: $color-white;
            border: 1px solid $color-rule;
            color: $color-danger;
            &:focus {
                background-color: $color-white;
                border: 1px solid $color-rule;
                color: $color-danger;
            }
            &:hover, &:active, &.active {
                background-color: $color-white;
                border: 1px solid $color-danger;
                color: $color-danger;
            }
        }
        &.btn-white-success {
            background-color: $color-white;
            border: 1px solid $color-rule;
            color: $color-success;
            &:focus {
                background-color: $color-white;
                border: 1px solid $color-rule;
                color: $color-success;
            }
            &:hover, &:active, &.active {
                background-color: $color-white;
                border: 1px solid $color-success;
                color: $color-success;
            }
        }
        &.btn-success {
            background-color: $color-success;
            border: 1px solid $color-success;
            color: $color-white;
            &:focus {
                background-color: $color-success;
                color: $color-white;
            }
            &:hover, &:active, &.active {
                background-color: $color-success;
                color: $color-white;
            }
        }
        &.btn-danger {
            background-color: $color-danger;
            border: 1px solid $color-danger;
            color: $color-white;
            &:focus {
                background-color: $color-danger;
                color: $color-white;
            }
            &:hover, &:active, &.active {
                background-color: $color-danger;
                color: $color-white;
            }
        }
        &.btn-special {
            background-color: $color-white;
            border: none;
            color: $color-primary;
            transition: all .1s ease;
            &:focus {
                background-color: $color-white;
                box-shadow: -5px 5px 0px $color-gold !important;
                color: $color-primary;
                opacity: 1;
            }
            &:hover, &:active, &.active {
                background-color: $color-white;
                box-shadow: -5px 5px 0px $color-gold;
                color: $color-primary;
                opacity: 1;
                transform: translate(5px, -5px);
            }
        }
        &.btn-custom {
            color: $color-white;
            &:focus {
                color: $color-white;
            }
            &:hover, &:active, &.active {
                color: $color-white;
            }
        }
    }

</style>

<script>

export default {

    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        size: {
            type: String,
            default: 'md',
        },
        icon: {
            type: String,
        },
        iconColor: {
            type: String,
        },
        arrow: {
            type: Boolean,
            default: false,
        },
        theme: {
            type: String,
            default: 'primary',
        },
        color: {
            type: String,
            default: null,
        },
        btnRounded: {
            type: Boolean,
            default: false,
        },
        btnBlock: {
            type: Boolean,
            default: false,
        },
        active: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        ariaLabel: {
            type: String,
            default: null,
        },
        ariaExpanded: {
            type: [Boolean, String],
            default: null,
        },
        ariaHaspopup: {
            type: [Boolean, String],
            default: null,
        },
    },

    computed: {

        classes() {
            return {
                // Sizes
                'btn-xs': this.size === 'xs',
                'btn-sm': this.size === 'sm',
                'btn-md': this.size === 'md',
                'btn-lg': this.size === 'lg',
                'btn-xl': this.size === 'xl',
                // Themes
                'btn-primary': this.theme === 'primary',
                'btn-danger': this.theme === 'danger',
                'btn-success': this.theme === 'success',
                'btn-trans': this.theme === 'trans',
                'btn-white': this.theme === 'white',
                'btn-white-primary': this.theme === 'white-primary',
                'btn-white-danger': this.theme === 'white-danger',
                'btn-white-success': this.theme === 'white-success',
                'btn-special': this.theme === 'special',
                'btn-custom': this.theme === 'custom',
                // Utilities
                'btn-rounded': this.btnRounded,
                'btn-block': this.btnBlock,
                'active': this.active,
            };
        },

        styles() {
            return {
                'background-color': this.theme === 'custom' && this.color ? this.color : null,
            };
        },

        iconMargin() {
            if (this.size === 'xs') {
                return 3;
            }
            if (this.size === 'sm') {
                return 5;
            }
            return 10;
        },

    },

}

</script>
